import {
  createAction,
  createAsyncThunk,
  createReducer,
  createSelector,
  type PayloadAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import invariant from "tiny-invariant";

import { getSmilesAction } from "~/actions/smiles";
import { fetchWithAuth } from "~/common/fetch";
import { isUser } from "~/common/user-shape";
import { API_LOGIN, API_WHOAMI } from "~/config";
import type { TSmiles } from "~/reducers/dashboard";
import { getBanners } from "~/slices/banners";
import type { RootState } from "~/store";
import type { MutableQueryState } from "~/types/redux";
import type { TUser } from "~/types/user";

const initialState: MutableQueryState<Partial<TUser>> = {
  status: "idle",
  data: undefined,
  error: undefined,
};

type UserResponse = TUser & { smiles?: Partial<TSmiles> };

export const userLoggedInSuccess = createAction<TUser>("USER_LOGGED_IN_SUCCESS");

export const initializeUser = createAsyncThunk("user/initialize", async (_, { dispatch }) => {
  const response = await fetchWithAuth(API_WHOAMI, {}, { redirectToLogin: false });

  if (!response.ok) {
    throw new Error(`Error occurred trying to fetch user: ${response.statusText}`);
  }

  const jsonUnknown: unknown = await response.json();
  invariant(isUser(jsonUnknown), `${API_LOGIN} - unexpected json received`);
  const json = jsonUnknown as UserResponse;

  const { smiles = {}, ...user } = json;
  dispatch(getSmilesAction(smiles));
  // Navigation links and badge counts depend on smiles data during initial user setup. The API embeds
  // that data in the user response; the frontend keeps it in the dashboard slice.

  Sentry.setUser({ id: user.account_id });

  return json;
});

export const loginUser = createAsyncThunk(
  "user/login",
  async ({ login, password }: { login: string; password: string }, { dispatch }) => {
    const response = await fetchWithAuth(API_LOGIN, {
      method: "POST",
      body: JSON.stringify({ login: login, password: password }),
    });

    if (!response.ok) {
      throw new Error(`Error occurred trying to login user: ${response.statusText}`);
    }

    const jsonUnknown: unknown = await response.json();
    invariant(isUser(jsonUnknown), `${API_LOGIN} - unexpected json received`);
    const json = jsonUnknown as UserResponse;

    const { smiles = {}, ...user } = json;
    dispatch(getSmilesAction(smiles));

    Sentry.setUser({ id: user.account_id });

    await dispatch(getBanners());

    return json;
  },
);

const selectUserData = (state: RootState) => state.user.data;
export const selectUser = createSelector([selectUserData], (userData) => userData ?? {});
export const selectUserIsLoading = (state: RootState): boolean => state.user.status == "loading";

export const user = createReducer(initialState, (builder) =>
  builder
    .addCase(initializeUser.pending, setLoading)
    .addCase(initializeUser.fulfilled, setSuccess)
    .addCase(initializeUser.rejected, (state, action) => setError(state, action.error))
    .addCase(loginUser.fulfilled, setSuccess)
    .addCase(loginUser.rejected, (state, action) => setError(state, action.error))
    .addCase(userLoggedInSuccess, setSuccess),
);

function setLoading(state: typeof initialState) {
  state.status = "loading";
  state.data = undefined;
  state.error = undefined;
}

function setSuccess(state: typeof initialState, action: PayloadAction<TUser>) {
  state.status = "success";
  state.data = action.payload;
  state.error = undefined;
}

function setError(state: typeof initialState, error: unknown) {
  state.status = "error";
  state.data = undefined;
  state.error = error;
}
